import React, { useMemo, useRef, useCallback, useState, useEffect } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import {
    DataGrid,
    Column,
    Scrolling,
    FilterRow,
    LoadPanel,
    Selection,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { alert } from "devextreme/ui/dialog";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";

function UpdateRequisitionDetailsPopup(props) {
    const { showPopup = {}, setShowPopup, onSuccess, oidc } = props;
    const visible = showPopup.visible === true;
    const accessToken = oidc?.user?.access_token;

    const dataGridRef = useRef(null);
    // Delphi OMD0034 InitializeForm: the third banner line reflects the consignee Item Warehouse scope.
    const [warehouseScopeLine, setWarehouseScopeLine] = useState(MESSAGES.REQUISITION_DETAILS_ALL_WAREHOUSES);
    // Delphi OMD0034 OpenQuery: the banner and the OK button both react to the grid's row count.
    // null = not loaded yet, so the normal banner shows until the first load finishes.
    const [recordCount, setRecordCount] = useState(null);

    const dataSource = useMemo(() => {
        return createStore({
            key: "Rowid",
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetOrderDetails}`,
            loadParams: {
                ItemCode: showPopup.ItemCode,
                ConsigneeRowId: showPopup.consigneeRowId,
            },
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                    access_token: accessToken,
                };
            },
        });
    }, [showPopup.ItemCode, showPopup.consigneeRowId, accessToken]);

    useEffect(() => {
        if (!visible) return;
        const consigneeRowId = showPopup.consigneeRowId;
        if (!consigneeRowId) {
            setWarehouseScopeLine(MESSAGES.REQUISITION_DETAILS_ALL_WAREHOUSES);
            return;
        }
        let cancelled = false;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetRequisitionWarehouse}`, {
                params: { consigneeRowId },
                headers: { Authorization: `Bearer ${accessToken}`, access_token: accessToken },
            })
            .then((res) => {
                if (cancelled) return;
                const { applied, warehouseCode } = res.data || {};
                setWarehouseScopeLine(
                    applied && warehouseCode
                        ? MESSAGES.REQUISITION_DETAILS_WAREHOUSE_ONLY.replace("{0}", warehouseCode)
                        : MESSAGES.REQUISITION_DETAILS_ALL_WAREHOUSES
                );
            })
            .catch(() => {
                if (!cancelled) setWarehouseScopeLine(MESSAGES.REQUISITION_DETAILS_ALL_WAREHOUSES);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, showPopup.consigneeRowId, accessToken]);

    const handleHiding = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const handleSetReceived = useCallback(async () => {
        const grid = dataGridRef.current?.instance();
        if (!grid) return;
        const selectedRows = grid.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) {
            alert(MESSAGES.NO_RECORDS_SELECTED, MESSAGES.INFORMATION);
            return;
        }
        const payload = selectedRows.map((r) => ({
            Rowid: r.Rowid,
        }));
        grid.beginCustomLoading("Processing");
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.SetOrderDetailsReceived}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    },
                }
            );
            const { success, message } = res.data || {};
            if (success) {
                alert(message || MESSAGES.RECORDS_PROCESSED, MESSAGES.SUCCESS);
                if (onSuccess) onSuccess();
                setShowPopup((prev) => ({ ...prev, visible: false }));
            } else {
                alert(message || MESSAGES.ERROR, MESSAGES.ERROR);
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
        } finally {
            grid.endCustomLoading();
        }
    }, [accessToken, onSuccess, setShowPopup]);

    if (!visible) return null;

    const currencyFormat = { type: "currency", precision: 2, currency: "USD", useGrouping: true };
    // Delphi OMD0034: on an empty result it clears the help text down to "NO RECORDS FOUND!" and disables OK.
    const noRecords = recordCount === 0;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={showPopup.title ?? MESSAGES.REQUISITION_DETAIL_VIEWER_TITLE}
            container="#root"
            width="85vw"
            height="80vh"
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <div
                    style={{
                        padding: "8px 10px",
                        marginBottom: 8,
                        background: "#fffbe6",
                        border: "1px solid #ffe58f",
                        borderRadius: 4,
                        textAlign: "center",
                        fontSize: 13,
                        lineHeight: 1.4,
                    }}
                >
                    {noRecords ? (
                        <strong>{MESSAGES.REQUISITION_DETAILS_NO_RECORDS_FOUND}</strong>
                    ) : (
                        <>
                            {MESSAGES.REQUISITION_DETAILS_BANNER_LINE1}
                            <br />
                            {MESSAGES.REQUISITION_DETAILS_BANNER_LINE2}
                            <br />
                            {warehouseScopeLine}
                        </>
                    )}
                </div>
                <DataGrid
                    ref={dataGridRef}
                    dataSource={dataSource}
                    columnAutoWidth={true}
                    allowColumnResizing={true}
                    allowColumnReordering={true}
                    hoverStateEnabled={true}
                    showColumnLines={true}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    paging={false}
                    style={{ flex: "1 1 auto", minHeight: 0 }}
                    onExporting={(e) => {
                        if (window.handleDevExtremeDataGridExport) {
                            window.handleDevExtremeDataGridExport(e);
                        }
                    }}
                    onContentReady={(e) => {
                        // Skip while the load is still in flight so the empty-state does not flash first.
                        if (e.component.getDataSource()?.isLoading()) return;
                        setRecordCount(e.component.getVisibleRows().length);
                    }}
                >
                    <Scrolling mode="standard" />
                    <LoadPanel enabled={true} />
                    <FilterRow visible={true} />
                    <Selection mode="multiple" showCheckBoxesMode="always" />
                    <Column dataField="Rowid" visible={false} />
                    <Column dataField="RecordNumber" caption="Record #" allowEditing={false} />
                    <Column dataField="Description" caption="Description" allowEditing={false} />
                    <Column dataField="WarehouseCode" caption="WH" allowEditing={false} />
                    <Column dataField="ItemCode" caption="Item" allowEditing={false} />
                    <Column dataField="LotCode" caption="Lot" allowEditing={false} />
                    <Column dataField="VendorID" caption="Vendor" allowEditing={false} />
                    <Column dataField="RequiredQuantity" caption="Required" dataType="number" format={{ type: "fixedPoint", precision: 2 }} allowEditing={false} />
                    <Column dataField="OrderedQuantity" caption="Ordered" dataType="number" format={{ type: "fixedPoint", precision: 2 }} allowEditing={false} />
                    <Column dataField="UnitCostOriginal" caption="Unit Cost" dataType="number" format={currencyFormat} allowEditing={false} />
                    <Column dataField="Amount" caption="Amount" dataType="number" format={currencyFormat} allowEditing={false} />
                    <GridToolbar>
                        <GridToolbarItem name="exportButton" />
                        <GridToolbarItem location="after">
                            <Button
                                text={MESSAGES.SET_SELECTED_AS_RECEIVED}
                                type="default"
                                disabled={noRecords}
                                onClick={handleSetReceived}
                            />
                        </GridToolbarItem>
                    </GridToolbar>
                </DataGrid>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(UpdateRequisitionDetailsPopup);
